import { useEffect, useState, useCallback, useRef } from "react";
import api, { fmtEUR, fmtDate } from "@/lib/api";
import { monthOptions } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClipboardCheck, Plus, Trash2, Paperclip, AlertTriangle, CheckCircle2, RefreshCw, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const DOC_TYPES = {
  albaran: "Albarán pendiente de pago",
  efectivo: "Factura pagada en efectivo",
  tarjeta: "Factura pagada con tarjeta",
};

const Figure = ({ label, value, testid, accent }) => (
  <div className="border rounded-xl p-3" data-testid={testid}>
    <div className="text-xs text-slate-500">{label}</div>
    <div className={`font-num font-bold text-lg mt-0.5 ${accent || ""}`}>{fmtEUR(value)}</div>
  </div>
);

export default function CortesCaja() {
  const { org, user, branchFilter } = useAuth();
  const branches = org?.branches || [];
  const [form, setForm] = useState({ branch_id: "", date: today(), counted_cash: "", notes: "" });
  const [items, setItems] = useState([]);
  const [expected, setExpected] = useState(null);
  const [closings, setClosings] = useState([]);
  const [month, setMonth] = useState("todos");
  const [saving, setSaving] = useState(false);
  const itemFileRef = useRef(null);
  const pendingItemIdx = useRef(null);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [editClosing, setEditClosing] = useState(null);
  const [delClosing, setDelClosing] = useState(null);
  const isAdmin = user?.role === "ADMIN";
  const canAct = ["ADMIN", "FINANCE", "MANAGER"].includes(user?.role);

  useEffect(() => {
    if (!form.branch_id && branches.length) {
      setForm((f) => ({ ...f, branch_id: branchFilter || branches[0].id }));
    }
  }, [branches, branchFilter, form.branch_id]);

  const loadExpected = useCallback(() => {
    if (!form.branch_id || !form.date) return;
    api.get(`/cash-closings/expected?branch_id=${form.branch_id}&date=${form.date}`)
      .then((r) => setExpected(r.data)).catch(() => setExpected(null));
  }, [form.branch_id, form.date]);
  useEffect(() => { loadExpected(); }, [loadExpected]);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (branchFilter) p.set("branch_id", branchFilter);
    if (month !== "todos") p.set("month", month);
    api.get(`/cash-closings?${p}`).then((r) => setClosings(r.data)).catch(() => {});
  }, [branchFilter, month]);
  useEffect(() => { load(); }, [load]);

  const uploadItemReceipt = async (e) => {
    const file = e.target.files?.[0];
    const i = pendingItemIdx.current;
    e.target.value = "";
    if (!file || i === null) return;
    setUploadingIdx(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/cash-closings/receipt-upload", fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setItems((prev) => prev.map((it, j) => (j === i
        ? { ...it, receipt_path: r.data.receipt_path, receipt_name: r.data.file_name, receipt_mime: r.data.receipt_mime }
        : it)));
      toast.success("Documento adjuntado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir el documento");
    } finally { setUploadingIdx(null); }
  };

  const syncNow = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const r = await api.post("/cash-closings/sync-loyverse", {}, { timeout: 120000 });
      toast.success(`Loyverse sincronizado — ${r.data.saved} ventas nuevas`);
      loadExpected();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al sincronizar Loyverse");
    } finally { setSyncing(false); }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/cash-closings/${editClosing.id}`, {
        counted_cash: Number(editClosing.counted_cash),
        notes: editClosing.notes || null,
      });
      toast.success("Corte actualizado");
      setEditClosing(null);
      load(); loadExpected();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar el corte");
    }
  };

  const deleteClosing = async () => {
    try {
      const r = await api.delete(`/cash-closings/${delClosing.id}`);
      toast.success(`Corte eliminado — revertidos ${r.data.movimientos_eliminados} movimientos y ${r.data.facturas_eliminadas} facturas`);
      setDelClosing(null);
      load(); loadExpected();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar el corte");
    }
  };

  const addItem = () => setItems([...items, { concept: "", amount: "", doc_type: "efectivo", receipt_path: "", receipt_name: "" }]);
  const setItem = (i, k, v) => setItems(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const removeItem = (i) => setItems(items.filter((_, j) => j !== i));
  const totalEfe = items.filter((i) => i.doc_type === "efectivo").reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const totalTar = items.filter((i) => i.doc_type === "tarjeta").reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const totalAlb = items.filter((i) => i.doc_type === "albaran").reduce((s, i) => s + (Number(i.amount) || 0), 0);

  const itemsOk = items.filter((i) => i.concept.trim() && Number(i.amount) > 0);
  const valid = form.branch_id && form.date && form.counted_cash !== "" && Number(form.counted_cash) >= 0
    && itemsOk.every((i) => i.receipt_path) && uploadingIdx === null;

  const esperadoFinal = expected ? Math.round((expected.esperado - totalEfe) * 100) / 100 : null;
  const descuadre = expected && form.counted_cash !== ""
    ? Math.round((Number(form.counted_cash) - esperadoFinal) * 100) / 100
    : null;

  const submit = async () => {
    if (saving) return;
    if (form.date > new Date().toISOString().slice(0, 10)) {
      toast.error("La fecha del corte no puede ser futura");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        branch_id: form.branch_id, date: form.date, counted_cash: Number(form.counted_cash),
        items: itemsOk
          .map((i) => ({ concept: i.concept.trim(), amount: Number(i.amount), doc_type: i.doc_type,
                         receipt_path: i.receipt_path, receipt_name: i.receipt_name, receipt_mime: i.receipt_mime })),
        notes: form.notes || undefined,
      };
      const r = await api.post("/cash-closings", payload);
      const d = r.data.descuadre;
      if (Math.abs(d) < 0.01) toast.success("Corte guardado — caja cuadrada");
      else toast.warning(`Corte guardado · descuadre ${d >= 0 ? "+" : ""}${fmtEUR(d)}`);
      setItems([]);
      setForm((f) => ({ ...f, counted_cash: "", notes: "" }));
      load(); loadExpected();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar el corte");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="cortes-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Corte de caja diario</h1>
          <p className="text-sm text-slate-500 mt-1">Registra las cifras del día y sube los tickets/facturas clasificados — se reflejan automáticamente en Facturas</p>
        </div>
        {canAct && (
          <Button data-testid="loyverse-sync-button" size="sm" variant="outline" className="gap-1.5"
            disabled={syncing} onClick={syncNow}>
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Sincronizando..." : "Sincronizar Loyverse"}
          </Button>
        )}
      </div>

      {canAct && (
        <Card data-testid="corte-form-card">
          <CardHeader><CardTitle className="text-base font-semibold font-heading">Corte del día</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-2 gap-3 max-w-md">
              <div className="space-y-1.5">
                <Label className="text-xs">Sucursal</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger data-testid="corte-branch" className="h-9"><SelectValue placeholder="Sucursal..." /></SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Fecha</Label>
                <Input data-testid="corte-date" type="date" value={form.date} max={today()} className="h-9"
                  onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
            </div>

            {expected && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="corte-figures">
                <Figure label="Fondo de caja" value={expected.fondo_caja} testid="figure-fondo" />
                <Figure label="Ventas en efectivo (Loyverse)" value={expected.ventas_efectivo} testid="figure-ventas-efectivo" accent="text-green-700" />
                <Figure label="Ventas con tarjeta (Loyverse)" value={expected.ventas_tarjeta} testid="figure-ventas-tarjeta" />
                <Figure label="Gastos en efectivo" value={(expected.gastos_efectivo || 0) + totalEfe} testid="figure-gastos-efectivo" accent="text-red-700" />
                <Figure label="Gastos con tarjeta" value={totalTar} testid="figure-gastos-tarjeta" accent="text-red-700" />
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Tickets y facturas del día (con su documento)</Label>
                <Button data-testid="corte-add-item" size="sm" variant="outline" onClick={addItem} className="gap-1 h-7">
                  <Plus className="w-3.5 h-3.5" /> Añadir documento
                </Button>
              </div>
              {items.map((it, i) => (
                <div key={i} className="flex flex-wrap gap-2 items-center" data-testid={`corte-item-${i}`}>
                  <Input data-testid={`corte-item-concept-${i}`} placeholder="Concepto (proveedor / qué es...)" value={it.concept}
                    onChange={(e) => setItem(i, "concept", e.target.value)} className="h-9 flex-1 min-w-full sm:min-w-0" />
                  <Button type="button" size="sm" variant="outline" data-testid={`corte-item-receipt-${i}`}
                    disabled={uploadingIdx !== null}
                    className={`h-9 gap-1.5 ${it.receipt_path ? "text-green-700 border-green-200 bg-green-50" : "text-red-600 border-red-200"}`}
                    onClick={() => { pendingItemIdx.current = i; itemFileRef.current?.click(); }}>
                    <Paperclip className="w-3.5 h-3.5" />
                    {uploadingIdx === i ? "Subiendo..." : it.receipt_name || "Documento *"}
                  </Button>
                  <Input data-testid={`corte-item-amount-${i}`} type="number" step="0.01" min="0" placeholder="€" value={it.amount}
                    onChange={(e) => setItem(i, "amount", e.target.value)} className="h-9 w-24 font-num" />
                  <Select value={it.doc_type} onValueChange={(v) => setItem(i, "doc_type", v)}>
                    <SelectTrigger data-testid={`corte-item-type-${i}`} className="h-9 w-56"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="albaran" data-testid={`corte-item-type-albaran-${i}`}>{DOC_TYPES.albaran}</SelectItem>
                      <SelectItem value="efectivo" data-testid={`corte-item-type-efectivo-${i}`}>{DOC_TYPES.efectivo}</SelectItem>
                      <SelectItem value="tarjeta" data-testid={`corte-item-type-tarjeta-${i}`}>{DOC_TYPES.tarjeta}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" data-testid={`corte-item-del-${i}`} onClick={() => removeItem(i)} className="text-slate-400 px-2">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              <input ref={itemFileRef} type="file" accept="image/*,.pdf" className="hidden" data-testid="corte-item-file-input"
                onChange={uploadItemReceipt} />
              {items.some((i) => i.concept.trim() && Number(i.amount) > 0 && !i.receipt_path) && (
                <p className="text-xs text-red-500" data-testid="corte-receipt-required">Adjunta el documento de cada ticket/factura — es obligatorio y se guardará en Facturas.</p>
              )}
              {items.length === 0 && <p className="text-xs text-slate-400">Sin documentos — si no hubo gastos, solo cuenta el efectivo y guarda.</p>}
              {items.length > 0 && (
                <div className="text-xs text-slate-500 text-right space-x-4 font-num" data-testid="corte-items-totals">
                  {totalAlb > 0 && <span>Albaranes pendientes: <b>{fmtEUR(totalAlb)}</b></span>}
                  {totalEfe > 0 && <span>Pagadas efectivo: <b>{fmtEUR(totalEfe)}</b></span>}
                  {totalTar > 0 && <span>Pagadas tarjeta: <b>{fmtEUR(totalTar)}</b></span>}
                </div>
              )}
              {totalTar > 0 && (
                <p className="text-xs text-slate-400">Las facturas pagadas con tarjeta quedan en Facturas como pendientes hasta conciliarlas con el extracto bancario.</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div className="space-y-1.5">
                <Label className="text-xs">Efectivo contado en caja (€)</Label>
                <Input data-testid="corte-counted" type="number" step="0.01" min="0" value={form.counted_cash} className="h-9 font-num"
                  onChange={(e) => setForm({ ...form, counted_cash: e.target.value })} placeholder="0,00" />
              </div>
              {expected && (
                <div className="flex justify-between border rounded-xl px-3 py-2" data-testid="corte-expected-card">
                  <span className="text-xs text-slate-500 self-center">Esperado en caja<br />(fondo + ventas efvo. − gastos efvo.)</span>
                  <span className="font-num font-bold" data-testid="corte-expected-value">{fmtEUR(esperadoFinal)}</span>
                </div>
              )}
              {descuadre !== null && (
                <div className={`flex justify-between items-center border rounded-xl px-3 py-2 ${Math.abs(descuadre) < 1 ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"}`}
                  data-testid="corte-descuadre-preview">
                  <span className={`text-xs ${Math.abs(descuadre) < 1 ? "text-green-700" : "text-amber-700"}`}>Descuadre (informativo)</span>
                  <span className={`font-num font-bold inline-flex items-center gap-1 ${Math.abs(descuadre) < 1 ? "text-green-700" : "text-amber-700"}`}>
                    {Math.abs(descuadre) < 1 ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                    {descuadre >= 0 ? "+" : ""}{fmtEUR(descuadre)}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Notas (opcional)</Label>
              <Input data-testid="corte-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="h-9" placeholder="Incidencias del día..." />
            </div>

            <Button data-testid="corte-submit" onClick={submit} disabled={!valid || saving}
              className="w-full bg-[#D9381E] hover:bg-[#B91C1C] text-white gap-2">
              <ClipboardCheck className="w-4 h-4" /> {saving ? "Guardando..." : "Guardar corte de caja"}
            </Button>
          </CardContent>
        </Card>
      )}

      <Card data-testid="cortes-history-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 flex-wrap gap-2">
          <CardTitle className="text-base font-semibold font-heading">Cortes registrados</CardTitle>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger data-testid="cortes-month-filter" className="w-44 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los meses</SelectItem>
              {monthOptions().map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="p-0">
          <Table data-testid="cortes-table">
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Sucursal</TableHead>
                <TableHead className="text-right">Ventas efvo.</TableHead>
                <TableHead className="text-right">Ventas tarj.</TableHead>
                <TableHead className="text-right">Gastos efvo.</TableHead>
                <TableHead className="text-right">Gastos tarj.</TableHead>
                <TableHead className="text-right">Contado</TableHead>
                <TableHead className="text-right">Descuadre</TableHead>
                <TableHead className="text-right">Docs</TableHead>
                {isAdmin && <TableHead className="text-right"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {closings.map((c) => (
                <TableRow key={c.id} data-testid={`corte-row-${c.id}`}>
                  <TableCell className="text-sm whitespace-nowrap">{fmtDate(c.date)}</TableCell>
                  <TableCell className="text-sm">{c.branch_name}</TableCell>
                  <TableCell className="text-right font-num text-sm">{fmtEUR(c.ventas_efectivo)}</TableCell>
                  <TableCell className="text-right font-num text-sm">{fmtEUR(c.ventas_tarjeta)}</TableCell>
                  <TableCell className="text-right font-num text-sm">{fmtEUR(c.total_efectivo ?? c.gastos_efectivo)}</TableCell>
                  <TableCell className="text-right font-num text-sm">{fmtEUR(c.total_tarjeta)}</TableCell>
                  <TableCell className="text-right font-num text-sm font-semibold">{fmtEUR(c.counted_cash)}</TableCell>
                  <TableCell className="text-right">
                    <span className={`inline-flex items-center gap-1 font-num text-sm font-semibold ${Math.abs(c.descuadre) < 1 ? "text-green-700" : "text-amber-700"}`}>
                      {Math.abs(c.descuadre) < 1 ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                      {c.descuadre >= 0 ? "+" : ""}{fmtEUR(c.descuadre)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right text-xs text-slate-500">
                    {(c.items || []).length}
                    {(c.total_albaranes || 0) > 0 && <div className="text-[10px] text-slate-400 font-num" data-testid={`corte-albaranes-${c.id}`}>albaranes {fmtEUR(c.total_albaranes)}</div>}
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5">
                        <Button data-testid={`corte-edit-${c.id}`} size="sm" variant="outline" className="h-7 px-2"
                          onClick={() => setEditClosing({ ...c, counted_cash: String(c.counted_cash) })}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button data-testid={`corte-delete-${c.id}`} size="sm" variant="outline"
                          className="h-7 px-2 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setDelClosing(c)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {closings.length === 0 && (
                <TableRow><TableCell colSpan={isAdmin ? 10 : 9} className="text-center py-10 text-slate-400 text-sm">
                  Aún no hay cortes de caja.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!editClosing} onOpenChange={(o) => !o && setEditClosing(null)}>
        <DialogContent className="max-w-sm" data-testid="corte-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Editar corte — {editClosing?.branch_name} · {fmtDate(editClosing?.date)}</DialogTitle>
            <DialogDescription>Corrige el efectivo contado o las notas. Los documentos no se editan: para cambiarlos, elimina el corte y créalo de nuevo.</DialogDescription>
          </DialogHeader>
          {editClosing && (
            <div className="space-y-4">
              <div className="space-y-1.5"><Label className="text-xs">Efectivo contado (€)</Label>
                <Input data-testid="corte-edit-counted" type="number" step="0.01" value={editClosing.counted_cash}
                  onChange={(e) => setEditClosing({ ...editClosing, counted_cash: e.target.value })} className="h-9 font-num" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Notas</Label>
                <Input data-testid="corte-edit-notes" value={editClosing.notes || ""}
                  onChange={(e) => setEditClosing({ ...editClosing, notes: e.target.value })} className="h-9" /></div>
              <Button data-testid="corte-edit-save" onClick={saveEdit}
                disabled={editClosing.counted_cash === ""}
                className="w-full bg-[#D9381E] hover:bg-[#B91C1C] text-white">Guardar cambios</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delClosing} onOpenChange={(o) => !o && setDelClosing(null)}>
        <AlertDialogContent data-testid="corte-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar corte de caja</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el corte del {fmtDate(delClosing?.date)} ({delClosing?.branch_name}) y se revertirán sus efectos: movimientos de caja y facturas creadas desde sus documentos. No se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="corte-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="corte-delete-confirm" onClick={deleteClosing}
              className="bg-red-600 hover:bg-red-700 text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
